import type { Song, Album, Artist } from '@/types';

/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */

// YouTube Music response types are complex and dynamic, using any for parsing

interface ParseResult {
  songs: Song[];
  albums: Album[];
}

export function parseSearchResults(ytData: any, whitelistedIds: Set<string>, whitelistNames: string[] = []): ParseResult {
  const songs: Song[] = [];
  const albums: Album[] = [];
  const albumIds = new Set<string>(); // Track unique album IDs

  console.log('🔧 parseSearchResults called with whitelistedIds.size:', whitelistedIds.size);

  try {
    const contents =
      ytData?.contents?.tabbedSearchResultsRenderer?.tabs[0]?.tabRenderer?.content
        ?.sectionListRenderer?.contents || [];

    console.log('📋 Found', contents.length, 'sections to parse');

    // Store debug info globally so we can access it
    (window as any).lastParseDebug = { sections: contents.length, items: [] };

    for (const section of contents) {
      const shelfTitle = section?.musicShelfRenderer?.title?.runs?.[0]?.text || '';
      const items = section?.musicShelfRenderer?.contents || [];

      console.log('📂 Section:', shelfTitle, '- Items:', items.length);

      for (const item of items) {
        const data = item?.musicResponsiveListItemRenderer;
        if (!data) continue;

        const flexColumns = data?.flexColumns || [];
        const title = flexColumns[0]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs?.[0]?.text || 'Unknown';

        const thumbnails = data?.thumbnail?.musicThumbnailRenderer?.thumbnail?.thumbnails || [];
        const thumbnail = thumbnails.length > 0 ? thumbnails[thumbnails.length - 1].url : null;

        // Try multiple locations for browseId
        const browseId =
          flexColumns[0]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs?.[0]
            ?.navigationEndpoint?.browseEndpoint?.browseId ||
          data?.navigationEndpoint?.browseEndpoint?.browseId ||
          data?.overlay?.musicItemThumbnailOverlayRenderer?.content?.musicPlayButtonRenderer
            ?.playNavigationEndpoint?.browseEndpoint?.browseId;

        const subtitleText =
          flexColumns[1]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs
            ?.map((r: any) => r.text)
            .join('') || '';
        const isAlbumByType =
          subtitleText.toLowerCase().includes('album') ||
          subtitleText.toLowerCase().includes(' ep') ||
          shelfTitle.toLowerCase().includes('album');

        const inAlbumSection = shelfTitle.toLowerCase().includes('album');

        // Get videoId early for debug info
        const videoId =
          data?.playlistItemData?.videoId ||
          data?.overlay?.musicItemThumbnailOverlayRenderer?.content?.musicPlayButtonRenderer
            ?.playNavigationEndpoint?.watchEndpoint?.videoId;

        const itemDebug = {
          title: title.substring(0, 40),
          browseId: browseId || 'none',
          browseIdPrefix: browseId?.substring(0, 10) || 'none',
          isAlbumByType,
          inAlbumSection,
          subtitleText: subtitleText.substring(0, 60),
          shelfTitle,
          hasVideoId: !!videoId
        };
        (window as any).lastParseDebug.items.push(itemDebug);
        console.log('🔍 Item:', itemDebug);

        if ((browseId && browseId.startsWith('MPREb_')) || isAlbumByType || inAlbumSection) {
          console.log('💿 Potential album detected:', title, 'browseId:', browseId);

          // Accept albums with MPREb_ ID (standard album ID)
          // OR albums in album section with ANY browseId
          // OR items that explicitly say "Album" or "EP" in subtitle
          const hasValidAlbumId = browseId && (
            browseId.startsWith('MPREb_') ||
            (inAlbumSection && browseId) ||
            (isAlbumByType && browseId)
          );

          if (hasValidAlbumId) {
            // STRICT whitelist enforcement for albums too
            if (whitelistedIds.size === 0) {
              console.log('⏭️  Skipped album (no whitelist loaded):', title);
              continue;
            }

            const artistRuns =
              flexColumns[1]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];

            // Extract artist info including IDs for whitelist checking
            const albumArtists: Artist[] = [];
            for (const run of artistRuns) {
              if (run.text && run.text.trim() && run.text !== '•' &&
                  !run.text.toLowerCase().includes('album') &&
                  !run.text.toLowerCase().includes(' ep') &&
                  !run.text.match(/^\s*,\s*$/)) {
                const artistName = run.text.trim();
                const artistId = run.navigationEndpoint?.browseEndpoint?.browseId;
                if (artistName !== ',' && artistName !== '&') {
                  albumArtists.push({ name: artistName, id: artistId });
                }
              }
            }

            // Check if any album artist is in whitelist (by ID or name)
            const isAlbumWhitelisted = albumArtists.length > 0 && isWhitelisted(albumArtists, whitelistedIds, whitelistNames);

            if (isAlbumWhitelisted) {
              const artist = albumArtists.map(a => a.name).join(' ');
              console.log('✅ Album added:', title, 'by', artist);
              albums.push({
                title,
                artist: artist || 'Unknown Artist',
                thumbnail,
                albumId: browseId,
              });
            } else {
              console.log('⏭️  Skipped album (artist not in whitelist):', title, albumArtists.map(a => a.id));
            }
          } else {
            console.log('⏭️  Skipping album (no valid browseId):', title, 'browseId:', browseId);
          }
          continue;
        }

        // videoId already defined above for debug info
        if (videoId) {
          const artistRuns =
            flexColumns[1]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];
          const artists: Artist[] = [];

          // Extract both artists and albums from flexColumns[1] runs
          for (const run of artistRuns) {
            if (
              run.text &&
              run.text.trim() &&
              run.text !== '•' &&
              !run.text.match(/^\s*,\s*$/)
            ) {
              const artistName = run.text.trim();
              const browseId = run.navigationEndpoint?.browseEndpoint?.browseId;

              // Check if this is an album (browseId starts with MPREb_)
              if (browseId && browseId.startsWith('MPREb_') && !albumIds.has(browseId)) {
                const albumTitle = artistName;
                // Get artist name from previous runs (should be the first artist in the list)
                const artistForAlbum = artists.length > 0 ? artists[0]!.name : 'Unknown Artist';

                albums.push({
                  title: albumTitle,
                  artist: artistForAlbum,
                  thumbnail,
                  albumId: browseId,
                });
                albumIds.add(browseId);
                console.log('💿 Added album:', albumTitle, 'by', artistForAlbum, '(', browseId, ')');
              }
              // Otherwise, it might be an artist
              else if (
                artistName !== ',' &&
                artistName !== '&' &&
                !artistName.match(/^(feat\.|ft\.)$/i)
              ) {
                artists.push({ name: artistName, id: browseId });
              }
            }
          }

          // STRICT whitelist enforcement - NEVER include if whitelist is empty
          // This ensures nothing shows if whitelist fails to load
          if (whitelistedIds.size === 0) {
            console.log('⏭️  Skipped song (no whitelist loaded):', { title });
            continue;
          }

          // Check if ANY artist is in the whitelist (by ID or name)
          const isArtistWhitelisted = artists.length > 0 && isWhitelisted(artists, whitelistedIds, whitelistNames);

          if (isArtistWhitelisted) {
            const artistNames = artists.map((a) => a.name).join(', ');
            songs.push({ videoId, title, artist: artistNames, artists, thumbnail });
            console.log('➕ Added song:', { title, artist: artistNames, videoId });
          } else {
            console.log('⏭️  Skipped song (not in whitelist):', { title, artistsCount: artists.length, artistIds: artists.map(a => a.id) });
          }
        }
      }
    }
  } catch (error) {
    console.error('Parse error:', error);
  }

  console.log('✅ parseSearchResults complete:', {
    totalSongs: songs.length,
    totalAlbums: albums.length,
  });

  return { songs, albums };
}

export function parseAlbumTracks(ytData: any): Song[] {
  const tracks: Song[] = [];

  try {
    const contents =
      ytData?.contents?.singleColumnBrowseResultsRenderer?.tabs[0]?.tabRenderer?.content
        ?.sectionListRenderer?.contents || [];

    for (const section of contents) {
      const items =
        section?.musicShelfRenderer?.contents ||
        section?.musicPlaylistShelfRenderer?.contents ||
        [];

      for (const item of items) {
        const data = item?.musicResponsiveListItemRenderer;
        if (!data) continue;

        const videoId =
          data?.playlistItemData?.videoId ||
          data?.overlay?.musicItemThumbnailOverlayRenderer?.content?.musicPlayButtonRenderer
            ?.playNavigationEndpoint?.watchEndpoint?.videoId;
        if (!videoId) continue;

        const flexColumns = data?.flexColumns || [];
        const title =
          flexColumns[0]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs?.[0]?.text ||
          'Unknown';

        // Extract artist info
        const artistRuns =
          flexColumns[1]?.musicResponsiveListItemFlexColumnRenderer?.text?.runs || [];
        const artists: Artist[] = [];

        for (const run of artistRuns) {
          if (
            run.text &&
            run.text.trim() &&
            run.text !== '•' &&
            !run.text.match(/^\s*,\s*$/)
          ) {
            const artistName = run.text.trim();
            const artistId = run.navigationEndpoint?.browseEndpoint?.browseId;
            if (
              artistName !== ',' &&
              artistName !== '&' &&
              !artistName.match(/^(feat\.|ft\.)$/i)
            ) {
              artists.push({ name: artistName, id: artistId });
            }
          }
        }

        const thumbnails = data?.thumbnail?.musicThumbnailRenderer?.thumbnail?.thumbnails || [];
        const thumbnail = thumbnails.length > 0 ? thumbnails[thumbnails.length - 1].url : null;

        const artistNames = artists.map((a) => a.name).join(', ');
        tracks.push({ videoId, title, artist: artistNames, artists, thumbnail });
      }
    }
  } catch (error) {
    console.error('Parse album tracks error:', error);
  }

  return tracks;
}

export function isWhitelisted(
  artists: Artist[],
  whitelistedIds: Set<string>,
  whitelistNames: string[]
): boolean {
  if (!artists || artists.length === 0) return false;

  return artists.some((artist) => {
    // ID-based matching (fast) - check first
    if (artist.id && whitelistedIds.has(artist.id)) {
      return true;
    }

    // ALWAYS do name-based matching as well, since search results
    // may return different channel IDs than what's in the whitelist
    if (artist.name) {
      const lower = artist.name.toLowerCase().trim();
      const matched = whitelistNames.some((wl) => {
        const wlNorm = wl.replace(/[.,]/g, '');
        const lowerNorm = lower.replace(/[.,]/g, '');
        return lower === wl || lowerNorm === wlNorm;
      });
      if (matched) {
        return true;
      }
    }

    return false;
  });
}
